import AttachmentDataProviderScope from 'owa-service/lib/contract/AttachmentDataProviderScope';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import { convertToAttachmentDataProviderType } from 'owa-attachment-file-types';
import FileProviderItemViewState, {
    FileItemViewState,
    FileProviderItemType,
} from '../../store/schema/FileProviderItemViewState';
import { formatRelativeDate } from 'owa-observable-datetime';
import getIconCSSForFileProviderItem from '../../utils/getIconCSSForFileProviderItem';
import ItemRowData from '../../types/ItemRowData';
import OneDriveProScopeType from 'owa-service/lib/contract/OneDriveProScopeType';
import { ControlIcons } from 'owa-control-icons';
import { formatUserDate } from 'owa-datetime';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isOneDriveProScope, MailMessageDataProviderItem } from 'owa-fileprovider-items-service';
import { MailboxAttachmentItem } from 'owa-fileprovider-services';

const ODPSharedWithMeScopeName = 'ODPSharedWithMeScope';
const ODPSharedWithMeRESTScopeName = 'ODPSharedWithMeRESTScope';

interface ItemRowDataWithModifiedTime extends ItemRowData {
    name: string;
    modifiedTime: string;
}

interface ItemRowDataWithSize extends ItemRowDataWithModifiedTime {
    size: string;
}

interface ItemRowDataComplete extends ItemRowDataWithSize {
    modifiedBy: string;
}

interface ItemRowDataRecentReceivedFile extends ItemRowData {
    name: string;
    from: string;
    subject: string;
    size: string;
    receivedDate: string;
    locationIcon: string;
}

interface ItemRowSharedWithMeFile extends ItemRowDataWithModifiedTime {
    modifiedBy: string;
    sharedTime: string;
    sharedBy: string;
}

export interface ItemRowDataMail extends ItemRowData {
    from: string;
    subject: string;
    preview: string;
    receivedDate: string;
}

const fileProviderColumnMapping = {};
fileProviderColumnMapping[AttachmentDataProviderType.OneDrivePro] = getItemToRenderComplete;
fileProviderColumnMapping[AttachmentDataProviderType.OneDriveConsumer] = getItemToRenderComplete;
fileProviderColumnMapping[AttachmentDataProviderType.Dropbox] = getItemToRenderWithSize;
fileProviderColumnMapping[AttachmentDataProviderType.Box] = getItemToRenderComplete;
fileProviderColumnMapping[AttachmentDataProviderType.Facebook] = getItemToRenderWithModifiedTime;
fileProviderColumnMapping[AttachmentDataProviderType.Mailbox] = getItemToRenderRecentFile;
fileProviderColumnMapping[AttachmentDataProviderType.GDrive] = getItemToRenderWithSize;
fileProviderColumnMapping[AttachmentDataProviderType.MailMessage] = getItemToRenderMail;
fileProviderColumnMapping[AttachmentDataProviderType.WopiBox] = getItemToRenderWithSize;
fileProviderColumnMapping[AttachmentDataProviderType.WopiEgnyte] = getItemToRenderWithSize;
fileProviderColumnMapping[AttachmentDataProviderType.WopiDropbox] = getItemToRenderWithSize;

const fileProviderScopeColumnMapping = {};
fileProviderScopeColumnMapping[ODPSharedWithMeScopeName] = getItemToRenderWithModifiedTime;
fileProviderScopeColumnMapping[ODPSharedWithMeRESTScopeName] = getItemToRenderSharedWithMe;

/* Transforms the FileProviderItemViewState into data that needs to be rendered*/
function getItemToRender(item: FileProviderItemViewState, index: number): ItemRowData {
    return {
        key: item.key,
        icon: getIconCSSForFileProviderItem(item),
        metadata: {
            index: index,
            title: item.location || item.name,
        },
    };
}

function getItemToRenderWithModifiedTime(
    item: FileProviderItemViewState,
    index: number
): ItemRowDataWithModifiedTime {
    return {
        ...getItemToRender(item, index),
        name: item.name,
        modifiedTime: item.modifiedTime ? formatUserDate(item.modifiedTime) : '',
    };
}

function getItemToRenderWithSize(
    item: FileProviderItemViewState,
    index: number
): ItemRowDataWithSize {
    return {
        ...getItemToRenderWithModifiedTime(item, index),
        size:
            item.type === FileProviderItemType.File
                ? (item as FileItemViewState).sizeDisplayString
                : '',
    };
}

function getItemToRenderComplete(
    item: FileProviderItemViewState,
    index: number
): ItemRowDataComplete {
    return {
        ...getItemToRenderWithSize(item, index),
        modifiedBy: item.modifiedBy,
    };
}

function getItemToRenderRecentFile(
    item: FileProviderItemViewState,
    index: number
): ItemRowDataRecentReceivedFile {
    return {
        ...getItemToRender(item, index),
        name: item.name,
        locationIcon:
            item.location === null // if location is null, file is cloudy attachment
                ? ControlIcons.Attach
                : getProviderIconForRecentFile(item.model as MailboxAttachmentItem),
        size:
            item.location === null // if location is null, file is cloudy attachment
                ? (item as FileItemViewState).sizeDisplayString
                : '',
        from: item.from,
        subject: item.subject,
        receivedDate: item.receivedDate ? formatUserDate(item.receivedDate) : '',
    };
}

function getProviderIcon(providerType: AttachmentDataProviderType): string {
    switch (providerType) {
        case AttachmentDataProviderType.OneDrivePro:
        case AttachmentDataProviderType.OneDriveConsumer:
            return ControlIcons.OneDriveLogo;
        case AttachmentDataProviderType.Dropbox:
        case AttachmentDataProviderType.WopiDropbox:
            return ControlIcons.DropboxLogo;
        case AttachmentDataProviderType.Box:
        case AttachmentDataProviderType.WopiBox:
            return ControlIcons.BoxLogo;
        case AttachmentDataProviderType.GDrive:
            return ControlIcons.GoogleDriveLogo;
        case AttachmentDataProviderType.Facebook:
            return ControlIcons.FacebookLogo;
        case AttachmentDataProviderType.WopiEgnyte:
            return ControlIcons.EgnyteLogo;
        default:
            return ControlIcons.Cloud;
    }
}

function getProviderIconForRecentFile(mailboxAttachmentItem: MailboxAttachmentItem): string {
    const providerType: AttachmentDataProviderType = convertToAttachmentDataProviderType(
        mailboxAttachmentItem.ReferenceAttachmentProviderType
    );
    return getProviderIcon(providerType);
}

function getItemToRenderSharedWithMe(
    item: FileProviderItemViewState,
    index: number
): ItemRowSharedWithMeFile {
    return {
        ...getItemToRenderWithModifiedTime(item, index),
        modifiedBy: item.modifiedBy,
        sharedTime: item.sharedTime ? formatUserDate(item.sharedTime) : '',
        sharedBy: item.sharedBy,
    };
}

function getItemToRenderMail(item: FileProviderItemViewState, index: number): ItemRowDataMail {
    const model = item.model as MailMessageDataProviderItem;

    return {
        ...getItemToRender(item, index),
        metadata: {
            index: index,
            title: item.location || item.name,
            isUnread: !model.IsRead,
        },
        from: item.from,
        subject: item.subject,
        preview: model.MessagePreview,
        receivedDate: formatRelativeDate(item.receivedDate),
    };
}

export type ColumnRenderFunction = (item: FileProviderItemViewState, index: number) => ItemRowData;

export default function getColumnRenderFunction(
    fileProviderType: AttachmentDataProviderType,
    scope?: AttachmentDataProviderScope
): ColumnRenderFunction {
    const scopeMapping = scope != null ? getColumnMappingFromScope(scope) : null;
    if (scopeMapping in fileProviderScopeColumnMapping) {
        return fileProviderScopeColumnMapping[scopeMapping];
    }
    return fileProviderType in fileProviderColumnMapping
        ? fileProviderColumnMapping[fileProviderType]
        : getItemToRender;
}

function getColumnMappingFromScope(scope: AttachmentDataProviderScope): string {
    if (isOneDriveProScope(scope) && scope.Type === OneDriveProScopeType.SharedWithMe) {
        return isFeatureEnabled('doc-filePicker-sharedWithMeImprovements')
            ? ODPSharedWithMeRESTScopeName
            : ODPSharedWithMeScopeName;
    }

    return '';
}
